<template>
  <div>

    <!-- ── Hero ─────────────────────────────────────────────────────────── -->
    <section class="bg-[#111] text-white py-14 lg:py-20">
      <div class="max-w-screen-xl mx-auto px-6 text-center">
        <p v-if="page.eyebrow" class="text-[0.875rem] font-semibold text-[#FFD500] mb-4 tracking-[0.04em] uppercase">
          {{ page.eyebrow }}
        </p>
        <h1 class="text-[clamp(2rem,4vw,3rem)] font-black text-white leading-tight mb-5">
          {{ page.title || 'Hoe het werkt' }}
        </h1>
        <p v-if="page.intro" class="text-[1.125rem] text-[#bbb] max-w-[620px] mx-auto leading-relaxed">
          {{ page.intro }}
        </p>
      </div>
    </section>

    <!-- ── Stappen (uit WordPress content) ──────────────────────────────── -->
    <section class="py-16 lg:py-20 bg-white">
      <div class="max-w-screen-xl mx-auto px-6">

        <!-- Skeleton loader -->
        <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="n in 4" :key="n" class="animate-pulse">
            <div class="w-12 h-12 rounded-full bg-[#f0f0f0] mb-4"></div>
            <div class="h-5 bg-[#f0f0f0] rounded mb-3 w-3/4"></div>
            <div class="h-3 bg-[#f0f0f0] rounded mb-2"></div>
            <div class="h-3 bg-[#f0f0f0] rounded w-5/6"></div>
          </div>
        </div>

        <!-- Stappen - auto-parse uit WP h2/p structuur -->
        <div v-else-if="steps.length" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
          <div
            v-for="(step, i) in steps"
            :key="i"
            class="relative"
          >
            <!-- Verbindingslijn tussen stappen (desktop) -->
            <div
              v-if="i < steps.length - 1"
              class="hidden lg:block absolute top-6 left-[3.5rem] right-[-2rem] h-px bg-[#f0f0f0] z-0"
            ></div>

            <!-- Nummer -->
            <div class="relative z-10 w-12 h-12 rounded-full bg-[#FFD500] text-[#111] font-black text-[1.125rem] flex items-center justify-center mb-5 shadow-[0_2px_8px_rgba(255,213,0,0.3)]">
              {{ i + 1 }}
            </div>

            <h3 class="text-[1.0625rem] font-bold text-[#111] mb-2 leading-snug">{{ step.title }}</h3>
            <p class="text-[0.875rem] text-[#555] leading-relaxed">{{ step.body }}</p>
          </div>
        </div>

        <!-- Fallback: WP content als blok HTML -->
        <div
          v-else-if="page.content"
          class="wp-content prose max-w-none"
          v-html="page.content"
        ></div>

      </div>
    </section>

    <!-- ── Aanbevolen producten ──────────────────────────────────────────── -->
    <section class="py-16 lg:py-20 bg-[#f8f9fa]">
      <div class="max-w-screen-xl mx-auto px-6">

        <div class="text-center mb-10">
          <p class="text-[0.8125rem] font-bold uppercase tracking-[0.08em] text-[#FFD500] mb-2">Kom je niet uit?</p>
          <h2 class="text-[1.75rem] lg:text-[2.25rem] font-black text-[#111]">
            Onze meest gekozen producten
          </h2>
          <p class="text-[#666] mt-3 max-w-[480px] mx-auto text-[0.9375rem]">
            Hieronder vind je de producten waar onze klanten het meest tevreden over zijn.
          </p>
        </div>

        <!-- Product skeleton -->
        <div v-if="productsLoading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="n in 3" :key="n" class="bg-white rounded-2xl overflow-hidden animate-pulse">
            <div class="h-52 bg-[#f0f0f0]"></div>
            <div class="p-5">
              <div class="h-4 bg-[#f0f0f0] rounded mb-3 w-4/5"></div>
              <div class="h-3 bg-[#f0f0f0] rounded mb-4 w-3/5"></div>
              <div class="h-10 bg-[#f0f0f0] rounded"></div>
            </div>
          </div>
        </div>

        <!-- Product kaarten -->
        <div v-else-if="products.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <router-link
            v-for="p in products"
            :key="p.id"
            :to="`/product/${p.slug}`"
            class="bg-white rounded-2xl overflow-hidden border border-[#eee] hover:border-[#FFD500] hover:shadow-[0_8px_32px_rgba(0,0,0,0.1)] transition-all duration-300 no-underline group flex flex-col"
          >
            <!-- Afbeelding -->
            <div class="h-52 flex items-center justify-center bg-[#f8f9fa] p-6 overflow-hidden">
              <img
                v-if="p.thumbnail || p.images?.[0]?.src"
                :src="p.thumbnail || p.images?.[0]?.src"
                :alt="p.name"
                class="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              >
              <i v-else class="bi bi-box-seam text-[3rem] text-[#ddd]"></i>
            </div>

            <div class="p-5 flex flex-col flex-1">
              <!-- Categorie -->
              <span v-if="p.categories?.[0]" class="text-[0.7rem] font-bold uppercase tracking-wider text-[#FFD500] mb-1">
                {{ p.categories[0].name }}
              </span>

              <!-- Naam -->
              <h3 class="text-[0.9375rem] font-bold text-[#111] leading-snug mb-2 line-clamp-2">{{ p.name }}</h3>

              <!-- Prijs -->
              <div class="flex items-center gap-2 mt-auto pt-3 border-t border-[#f5f5f5]">
                <span class="text-[1.125rem] font-black text-[#111]">{{ formatPrice(p.price) }}</span>
                <span v-if="p.on_sale && p.regular_price" class="text-[0.8125rem] text-[#aaa] line-through">{{ formatPrice(p.regular_price) }}</span>
                <span
                  class="ml-auto inline-flex items-center gap-1.5 bg-[#FFD500] text-[#111] text-[0.8rem] font-bold px-4 py-2 rounded-lg group-hover:bg-[#ffb800] transition-colors"
                >
                  Bekijk <i class="bi bi-arrow-right"></i>
                </span>
              </div>
            </div>
          </router-link>
        </div>

        <!-- CTA -->
        <div class="text-center mt-10">
          <router-link
            to="/producten"
            class="inline-flex items-center gap-2 bg-[#111] text-white font-bold px-8 py-4 rounded-xl no-underline hover:bg-[#222] transition-colors text-[0.9375rem]"
          >
            Bekijk alle producten
            <i class="bi bi-arrow-right"></i>
          </router-link>
        </div>

      </div>
    </section>

    <!-- ── USP-balk ───────────────────────────────────────────────────────── -->
    <section class="py-12 bg-white border-t border-[#f0f0f0]">
      <div class="max-w-screen-xl mx-auto px-6">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          <div v-for="usp in usps" :key="usp.icon" class="flex flex-col items-center gap-2">
            <div class="w-10 h-10 rounded-full bg-[#fffbe6] flex items-center justify-center text-[#FFD500] text-[1.25rem]">
              <i :class="`bi ${usp.icon}`"></i>
            </div>
            <strong class="text-[0.875rem] text-[#111] font-bold">{{ usp.title }}</strong>
            <span class="text-[0.775rem] text-[#888]">{{ usp.sub }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ── CTA banner ────────────────────────────────────────────────────── -->
    <section class="py-14 bg-[#FFD500]">
      <div class="max-w-screen-xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-6">
        <div>
          <h2 class="text-[1.5rem] font-black text-[#111] mb-1">Wil je persoonlijk advies?</h2>
          <p class="text-[#333] text-[0.9375rem]">Onze experts helpen je kosteloos de juiste keuze te maken.</p>
        </div>
        <router-link
          to="/advies"
          class="shrink-0 inline-flex items-center gap-2 bg-[#111] text-white font-bold px-7 py-4 rounded-xl no-underline hover:bg-[#222] transition-colors whitespace-nowrap"
        >
          Gratis advies aanvragen <i class="bi bi-arrow-right"></i>
        </router-link>
      </div>
    </section>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'

// ── State ─────────────────────────────────────────────────────────────────────
const loading        = ref(true)
const productsLoading = ref(true)
const page           = ref({ title: '', eyebrow: '', intro: '', content: '' })
const steps          = ref([])
const products       = ref([])

// ── USPs ──────────────────────────────────────────────────────────────────────
const usps = [
  { icon: 'bi-truck',          title: 'Gratis verzending',  sub: 'Vanaf €50' },
  { icon: 'bi-shield-check',   title: '2 jaar garantie',    sub: 'Op alle producten' },
  { icon: 'bi-arrow-clockwise',title: '30 dagen retour',    sub: 'Zonder gedoe' },
  { icon: 'bi-headset',        title: 'Expert support',     sub: 'Ma–Vr 9–17 uur' },
]

// ── Helpers ───────────────────────────────────────────────────────────────────
import { formatPrice } from '@/utils/price'

/**
 * Parseer WordPress HTML naar stap-objecten.
 * Verwacht structuur: <h2>Staptitel</h2><p>Beschrijving</p>
 * Optioneel: eerste <p> van de pagina als intro (vóór eerste h2).
 */
function parseSteps(html) {
  if (!html) return []
  const div = document.createElement('div')
  div.innerHTML = html
  const result = []
  let current = null

  div.childNodes.forEach(node => {
    const tag = node.nodeName?.toLowerCase()
    if (tag === 'h2') {
      if (current) result.push(current)
      current = { title: node.textContent?.trim() || '', body: '' }
    } else if (tag === 'p' && current) {
      current.body += (current.body ? ' ' : '') + (node.textContent?.trim() || '')
    }
  })
  if (current) result.push(current)
  return result
}

// ── Data ophalen ──────────────────────────────────────────────────────────────
onMounted(async () => {
  // 1. WordPress pagina ophalen (slug: hoe-het-werkt)
  try {
    const res = await fetch('/api/wp/v2/pages?slug=hoe-het-werkt&_fields=title,content,excerpt,acf,meta')
    if (res.ok) {
      const data = await res.json()
      if (data?.[0]) {
        const wp = data[0]
        page.value = {
          title:   wp.title?.rendered   || 'Hoe het werkt',
          intro:   wp.excerpt?.rendered  ? wp.excerpt.rendered.replace(/<[^>]*>/g, '').trim() : '',
          content: wp.content?.rendered || '',
          // Optioneel: ACF veld 'eyebrow' voor het label boven de titel
          eyebrow: wp.acf?.eyebrow || 'In 4 eenvoudige stappen',
        }
        // Parseer h2/p naar stappenlijst
        steps.value = parseSteps(wp.content?.rendered || '')
      }
    }
  } catch (e) {
    console.error('[HoeHetWerkt] page fetch error:', e)
  } finally {
    loading.value = false
  }

  // 2. Aanbevolen producten ophalen (featured=true, max 3)
  try {
    const res = await fetch('/_wc/v3/products?featured=true&per_page=3&status=publish')
    if (res.ok) {
      const data = await res.json()
      // Vereenvoudig product-data (images url rewrite)
      products.value = (Array.isArray(data) ? data : []).map(p => ({
        ...p,
        thumbnail: p.images?.[0]?.src?.replace('https://admin.plug2charge.nl', '') || null,
      }))
    }
  } catch (e) {
    console.error('[HoeHetWerkt] products fetch error:', e)
  } finally {
    productsLoading.value = false
  }
})
</script>

<style scoped>
.wp-content :deep(h2) {
  font-size: 1.25rem;
  font-weight: 800;
  color: #111;
  margin-bottom: 0.75rem;
  margin-top: 2rem;
}
.wp-content :deep(h3) {
  font-size: 1.0625rem;
  font-weight: 700;
  color: #111;
  margin-bottom: 0.5rem;
  margin-top: 1.5rem;
}
.wp-content :deep(p)  { margin-bottom: 1rem; color: #555; line-height: 1.75; }
.wp-content :deep(ul),
.wp-content :deep(ol) { padding-left: 1.25rem; margin-bottom: 1rem; }
.wp-content :deep(li) { margin-bottom: 0.375rem; color: #555; }
.wp-content :deep(strong) { color: #111; }
</style>
